/**
 * Product-card gallery and app-neutral wishlist fallback.
 * Wishlist apps can cancel `lophiel:wishlist:toggle-request` and handle the
 * action themselves. Without an app, product IDs are stored in localStorage.
 */
(function () {
  const wishlistKey = 'lophiel:wishlist:v1';

  function readWishlist() {
    try {
      const value = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch (_) {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(wishlistKey, JSON.stringify(items));
    } catch (_) {}
  }

  function setWishlistState(button, selected) {
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    button.setAttribute('aria-label', selected ? button.dataset.removeLabel : button.dataset.addLabel);
  }

  function syncWishlistButtons() {
    const saved = new Set(readWishlist());
    document.querySelectorAll('[data-product-card-wishlist]').forEach((button) => {
      setWishlistState(button, saved.has(String(button.dataset.productId)));
    });
  }

  function selectSlide(gallery, selectedIndex) {
    const slides = gallery.querySelectorAll('[data-product-card-slide]');
    const dots = gallery.querySelectorAll('[data-product-card-dot]');

    slides.forEach((slide, index) => {
      const isActive = index === selectedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-before-active', index < selectedIndex);
      slide.classList.toggle('is-after-active', index > selectedIndex);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    dots.forEach((dot, index) => {
      const isActive = index === selectedIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function getActiveSlideIndex(gallery) {
    const slides = [...gallery.querySelectorAll('[data-product-card-slide]')];
    const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    return Math.max(0, activeIndex);
  }

  function setupGalleryDrag(gallery) {
    if (gallery.dataset.productCardGalleryReady === 'true') return;

    const media = gallery.querySelector('.lophiel-product-card__media');
    const slideCount = gallery.querySelectorAll('[data-product-card-slide]').length;
    if (!media || slideCount < 2) return;

    gallery.dataset.productCardGalleryReady = 'true';
    let activePointerId = null;
    let startX = 0;
    let currentX = 0;
    let hasDragged = false;
    let suppressNextClick = false;

    function resetDrag() {
      gallery.classList.remove('is-dragging');
      gallery.style.removeProperty('--product-card-drag-x');
    }

    media.addEventListener('dragstart', (event) => event.preventDefault());

    media.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      activePointerId = event.pointerId;
      startX = event.clientX;
      currentX = startX;
      hasDragged = false;
      gallery.classList.add('is-dragging');

      try {
        media.setPointerCapture(activePointerId);
      } catch (_) {}
    });

    media.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointerId) return;

      currentX = event.clientX;
      const deltaX = currentX - startX;
      if (Math.abs(deltaX) > 6) hasDragged = true;

      const visualOffset = Math.max(-80, Math.min(80, deltaX * 0.45));
      gallery.style.setProperty('--product-card-drag-x', `${visualOffset}px`);
    });

    function finishDrag(event, cancelled) {
      if (event.pointerId !== activePointerId) return;

      const endX = typeof event.clientX === 'number' ? event.clientX : currentX;
      const deltaX = endX - startX;

      if (!cancelled && Math.abs(deltaX) >= 42) {
        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = (getActiveSlideIndex(gallery) + direction + slideCount) % slideCount;
        selectSlide(gallery, nextIndex);
      }

      suppressNextClick = !cancelled && hasDragged;
      if (suppressNextClick) {
        window.setTimeout(() => {
          suppressNextClick = false;
        }, 0);
      }
      activePointerId = null;
      resetDrag();
    }

    media.addEventListener('pointerup', (event) => finishDrag(event, false));
    media.addEventListener('pointercancel', (event) => finishDrag(event, true));
    media.addEventListener('click', (event) => {
      if (!suppressNextClick) return;

      event.preventDefault();
      event.stopPropagation();
      suppressNextClick = false;
    });
  }

  function initialize(root) {
    root.querySelectorAll('[data-product-card-gallery]').forEach((gallery) => {
      selectSlide(gallery, 0);
      setupGalleryDrag(gallery);
    });
    syncWishlistButtons();
  }

  document.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-product-card-dot]');
    if (dot) {
      const gallery = dot.closest('[data-product-card-gallery]');
      if (gallery) selectSlide(gallery, Number(dot.dataset.productCardDot));
      return;
    }

    const button = event.target.closest('[data-product-card-wishlist]');
    if (!button) return;

    const product = {
      id: String(button.dataset.productId),
      handle: button.dataset.productHandle,
      url: button.dataset.productUrl,
    };
    const selected = button.getAttribute('aria-pressed') !== 'true';
    const request = new CustomEvent('lophiel:wishlist:toggle-request', {
      bubbles: true,
      cancelable: true,
      detail: { button, product, selected },
    });

    if (!button.dispatchEvent(request)) return;

    const saved = new Set(readWishlist());
    if (selected) saved.add(product.id);
    else saved.delete(product.id);
    writeWishlist([...saved]);
    syncWishlistButtons();

    document.dispatchEvent(
      new CustomEvent('lophiel:wishlist:changed', {
        detail: { product, selected, productIds: [...saved] },
      })
    );
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('storage', (event) => {
    if (event.key === wishlistKey) syncWishlistButtons();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }
})();
